import { RootState } from '@tabeeb/store'
import { createSelector } from 'reselect'
import { ContentSharingModelType } from '@tabeeb/contracts/main-api/swagger-main-api'
import {
  getIsContentReviewingSettingEnabled,
  getIsMergeForReviewEnabled,
  getSelectedTenantsForContentSharing,
} from '@tabeeb/modules/appConfigState/selectors'
import {
  getContentDescriptions,
  getReviewsList,
  getSelectedFormsReview,
  getSelectedFormsReviewer,
  getSelectedFormsReviewerContent,
  getSelectedFormsReviewerTenant,
  getTenantsListForReview,
} from '@tabeeb/modules/contentReviews/selectors'
import { getContentDescription, getContentId, getContentTenantId } from '@tabeeb/modules/shared/content/selectors'

export const getSelectedTenantForSharing = (state: RootState) => state.sessionsPage.selectedTenantForSharing
export const getSelectedOwnerForSharing = (state: RootState) => state.sessionsPage.selectedOwnerForSharing
export const getSelectedTargetContentForMerging = (state: RootState) =>
  state.sessionsPage.selectedTargetContentForMerging

export const getSourceContentReviews = createSelector([getReviewsList, getContentId], (reviewsList, contentId) => {
  return reviewsList.filter((r) => r.ContentId === contentId)
})

export const getTenantsListForSharing = createSelector(
  [
    getTenantsListForReview,
    getSelectedTenantsForContentSharing,
    (state: RootState, props: { contentSharingType: ContentSharingModelType }) => props.contentSharingType,
  ],
  (reviewTenantsList, sharingTenantsList, contentSharingType) => {
    const isForReview =
      contentSharingType === ContentSharingModelType.ForReview ||
      contentSharingType === ContentSharingModelType.MergeForReview

    return isForReview ? reviewTenantsList : sharingTenantsList
  }
)

export const getSelectedTenant = createSelector(
  [
    getSelectedFormsReview,
    getSelectedFormsReviewerTenant,
    getSelectedTenantForSharing,
    (state: RootState, props: { contentSharingType: ContentSharingModelType }) => props.contentSharingType,
  ],
  (selectedFormsReview, preselectedTenant, selectedTenant, contentSharingType) => {
    const isForReview =
      contentSharingType === ContentSharingModelType.ForReview ||
      contentSharingType === ContentSharingModelType.MergeForReview

    if (isForReview && selectedFormsReview) {
      return preselectedTenant
    }

    return selectedTenant
  }
)

export const getSelectedUser = createSelector(
  [
    getSelectedFormsReview,
    getSelectedFormsReviewer,
    getSelectedOwnerForSharing,
    (state: RootState, props: { contentSharingType: ContentSharingModelType }) => props.contentSharingType,
  ],
  (selectedFormsReview, preselectedUser, selectedUser, contentSharingType) => {
    const isForReview =
      contentSharingType === ContentSharingModelType.ForReview ||
      contentSharingType === ContentSharingModelType.MergeForReview

    if (isForReview && selectedFormsReview) {
      return preselectedUser
    }

    return selectedUser
  }
)

export const getSelectedContent = createSelector(
  [
    getSelectedFormsReview,
    getSelectedFormsReviewerContent,
    getSelectedTargetContentForMerging,
    (state: RootState, props: { contentSharingType: ContentSharingModelType }) => props.contentSharingType,
  ],
  (selectedFormsReview, preselectedContent, selectedContent, contentSharingType) => {
    const isForReview =
      contentSharingType === ContentSharingModelType.ForReview ||
      contentSharingType === ContentSharingModelType.MergeForReview

    if (isForReview && selectedFormsReview?.Type === ContentSharingModelType.MergeForReview) {
      return preselectedContent
    }

    return selectedContent
  }
)

export const getSourceReviewsByType = createSelector(
  [
    getSourceContentReviews,
    (state: RootState, props: { contentSharingType: ContentSharingModelType }) => props.contentSharingType,
  ],
  (reviewsList, sharingType) => {
    return reviewsList.filter((r) => r.Type === sharingType)
  }
)

export const getCanRenameContentDescriptionForTenant = createSelector(
  [getTenantsListForReview, getContentTenantId, (state: RootState, props: { tenantId: number }) => props.tenantId],
  (tenantList, contentTenantId, selectedTenantId) => {
    if (!selectedTenantId || contentTenantId === selectedTenantId) return false

    const selectedTenant = tenantList.find((t) => t.Id === selectedTenantId)
    if (!selectedTenant) return false

    const isVisible = selectedTenant.IsNamingConventionEnabled && selectedTenant.IsNamingConventionForReviewEnabled
    return isVisible
  }
)

export const getContentDescriptionForTenant = createSelector(
  [getContentDescriptions, getContentDescription, (state: RootState, props: { tenantId: number }) => props.tenantId],
  (contentDescriptionsInOtherTenants, descriptionInOriginalTenant, tenantId) => {
    if (!tenantId) return false

    const descriptionInOtherTenant = contentDescriptionsInOtherTenants.find((d) => d.TenantId === tenantId)

    if (!descriptionInOtherTenant) {
      return descriptionInOriginalTenant
    }

    return descriptionInOtherTenant.Description
  }
)

export const getContentReviewDestinationOptions = createSelector(
  [getIsContentReviewingSettingEnabled, getIsMergeForReviewEnabled],
  (isReviewSettingEnabled, isMergeReviewSettingEnabled) => {
    const options: { type: ContentSharingModelType; description: string }[] = []

    if (isReviewSettingEnabled) {
      options.push({ type: ContentSharingModelType.ForReview, description: 'Create review in new session' })
    }

    if (isMergeReviewSettingEnabled) {
      options.push({
        type: ContentSharingModelType.MergeForReview,
        description: 'Merge for review into an existing session',
      })
    }

    return options
  }
)

export const getContentSharingDestinationOptions = createSelector(
  [
    getContentReviewDestinationOptions,
    (state: RootState, props: { contentSharingType: ContentSharingModelType }) => props.contentSharingType,
  ],
  (contentReviewDestinationOptions, contentSharingType) => {
    const options: { type: ContentSharingModelType; description: string }[] = []

    const isSharing =
      contentSharingType === ContentSharingModelType.SharedByOwner ||
      contentSharingType === ContentSharingModelType.ContentsMerging

    const isReview =
      contentSharingType === ContentSharingModelType.ForReview ||
      contentSharingType === ContentSharingModelType.MergeForReview

    if (!isSharing && !isReview) {
      return options
    }

    if (isSharing) {
      options.push({
        type: ContentSharingModelType.SharedByOwner,
        description: 'Create new session in destination tenant',
      })
      options.push({
        type: ContentSharingModelType.ContentsMerging,
        description: 'Share to existing session in destination tenant',
      })
    } else {
      options.push(...contentReviewDestinationOptions)
    }

    return options
  }
)
